<template>
  <div>
    <!-- Критический оверлей (блокирует интерфейс) -->
    <div v-if="momentaryStore.isCriticalLoading" class="critical-loading-overlay">
      <div class="critical-loading-content">
        <!-- Стильный прогресс-бар -->
        <div class="stylish-progress-container">
          <div class="progress-label mb-3">
            <h5 class="mb-1">
              {{ momentaryStore.currentCriticalTask ? momentaryStore.currentCriticalTask.label : $t("general.loading", "Loading...") }}
            </h5>
            <small class="text-muted">Please wait while we process your request</small>
          </div>

          <div class="stylish-progress-bar">
            <div class="progress-track">
              <div class="progress-fill" />
              <div class="progress-glow" />
            </div>
          </div>

          <div class="loading-dots mt-3">
            <span />
            <span />
            <span />
          </div>

          <small v-if="momentaryStore.criticalLoadingTasks.length > 1" class="text-muted mt-2 d-block">{{ momentaryStore.criticalLoadingTasks.length }} tasks remaining</small>
        </div>
      </div>
    </div>

    <!-- Контент страницы -->
    <div :class="{ 'content-blocked': momentaryStore.isCriticalLoading }">
      <slot />
    </div>

    <!-- Фоновый индикатор загрузки (не блокирует интерфейс) -->
    <transition name="slide-down">
      <div v-if="momentaryStore.isBackgroundLoading" class="background-loading-indicator">
        <div class="loading-content">
          <b-spinner small class="mr-2" />
          <span v-if="momentaryStore.currentBackgroundTask">
            {{ momentaryStore.currentBackgroundTask.label }}
          </span>
          <b-badge v-if="momentaryStore.backgroundLoadingTasks.length > 1" variant="secondary" class="ml-2">
            {{ momentaryStore.backgroundLoadingTasks.length }}
          </b-badge>
          <b-button variant="link" size="sm" class="ml-2 p-0 text-decoration-none" @click="showDetails = !showDetails">
            <b-icon :icon="showDetails ? 'chevron-up' : 'chevron-down'" />
          </b-button>
        </div>

        <!-- Детальная информация -->
        <b-collapse v-model="showDetails">
          <div class="loading-details mt-2">
            <div v-for="task in momentaryStore.backgroundLoadingTasks" :key="task.key" class="loading-task mb-1">
              <small class="text-muted">{{ task.label }}</small>
              <b-progress v-if="task.progress > 0" :value="task.progress" :max="100" size="sm" class="mt-1" />
            </div>
          </div>
        </b-collapse>
      </div>
    </transition>
  </div>
</template>

<script>
import { mapStores } from "pinia";
import { useMomentaryStore } from "@/stores/momentary";

export default {
  name: "LoadingIndicator",
  data() {
    return {
      showDetails: false,
    };
  },
  computed: {
    ...mapStores(useMomentaryStore),
  },
  watch: {
    "momentaryStore.isBackgroundLoading"(newVal) {
      if (!newVal) {
        this.showDetails = false;
      }
    },
  },
};
</script>

<style scoped>
/* Критический оверлей */
.critical-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1020;
  animation: fadeIn 0.3s ease-out;
}

.critical-loading-content {
  text-align: center;
  padding: 3rem 2rem;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(0, 0, 0, 0.05);
  max-width: 400px;
  width: 90%;
}

/* Стильный прогресс-бар */
.stylish-progress-container {
  width: 100%;
}

.progress-label h5 {
  color: #2c3e50;
  font-weight: 600;
  margin: 0;
}

.stylish-progress-bar {
  margin: 1.5rem 0;
  width: 100%;
}

.progress-track {
  position: relative;
  width: 100%;
  height: 8px;
  background: #ecf0f1;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
}

.progress-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, #27ae60, #2ecc71, #58d68d);
  border-radius: 4px;
  animation: fillProgress 2s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(46, 204, 113, 0.3);
}

.progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  border-radius: 4px;
  animation: glowMove 2s ease-in-out infinite;
}

/* Анимация заполнения прогресс-бара */
@keyframes fillProgress {
  0% {
    width: 0;
  }
  50% {
    width: 100%;
  }
  100% {
    width: 100%;
  }
}

@keyframes glowMove {
  0% {
    width: 0;
    left: 0;
  }
  50% {
    width: 30%;
    left: 70%;
  }
  100% {
    width: 0;
    left: 100%;
  }
}

/* Анимированные точки */
.loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  background: #27ae60;
  border-radius: 50%;
  animation: dotPulse 1.5s ease-in-out infinite;
}

.loading-dots span:nth-child(1) {
  animation-delay: 0s;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dotPulse {
  0%,
  60%,
  100% {
    transform: scale(1);
    opacity: 0.7;
  }
  30% {
    transform: scale(1.3);
    opacity: 1;
  }
}

/* Анимация появления */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.content-blocked {
  pointer-events: none;
  user-select: none;
}

.background-loading-indicator {
  position: fixed;
  top: 70px; /* Сдвигаем вниз, чтобы не перекрывать navbar */
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1035; /* Выше чем у navbar, но ниже модалов */
  min-width: 250px;
  max-width: 350px;
}

.loading-content {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.loading-details {
  border-top: 1px solid #dee2e6;
  padding-top: 8px;
}

.loading-task {
  padding: 4px 0;
}

/* Анимации */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.3s ease;
  transform-origin: top;
}

.slide-down-enter-from {
  opacity: 0;
  transform: translateY(-20px) scale(0.9);
}

.slide-down-leave-to {
  opacity: 0;
  transform: translateY(-20px) scale(0.9);
}
</style>
